import { createContext, useContext } from "react";
import type { UserRow } from "../api/session";

/**
 * Auth state shared across the app:
 *  - `loading` — we haven't checked the session yet on cold start.
 *  - `anon`    — no valid session; only public routes are accessible.
 *  - `user`    — logged-in user; the `user` row is the latest snapshot from
 *                the server (or from a fresh login response).
 *
 * Login screens (Login.tsx, future Apple/email forms) must call `setUser`
 * after obtaining a session — otherwise the App-level guards still think
 * we're anonymous and bounce protected routes back to /login.
 */
export type AuthState =
  | { kind: "loading" }
  | { kind: "anon" }
  | { kind: "user"; user: UserRow };

type AuthContextValue = {
  auth: AuthState;
  /** Replace the current user (e.g. after Google/Telegram login or profile update). */
  setUser: (user: UserRow) => void;
  /** Clear the session (used by Logout). */
  clearUser: () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider = AuthContext.Provider;

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
